/**
 * COMPONENT: StockMovementPopup.jsx
 * Pilcore — Stock Movements (despacho de requisiciones)
 * Muestra la requisición aprobada + permite introducir cantidades entregadas.
 */

import React, { useCallback, useEffect, useState } from "react";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useStockOperationAccess from "../useStockOperationAccess";
import StockOperationsDialog from "./StockOperationsDialog";
import { useDocumentDialog } from "./PurchasingDocumentDialog";
import { getStockPopupTranslator } from "./stockPopupMessages";
import { canUseStockLocation } from "../stockRequisitionPermissions";
import {
  formatStockOpsCurrency,
  formatStockOpsNumber,
} from "../stockOperationsUi";

const locationStockFor = (context, locationId, itemId) =>
  locationId && context.items.some((item) => Number(item.id) === Number(itemId))
    ? Number(context.balances[locationId]?.[itemId] || 0) : null;

const invalidMovementMessage = "This delivery's stock movement link is missing or inconsistent. Review its traceability before retrying.";
const dispatchErrorMessages = {
  LOCATION_LEDGER_DISCONTINUITY: "Stock locations need administrative review before this operation can continue. Contact the administrator.",
  LOCATION_LEDGER_EVENT_CONFLICT: "Stock locations need administrative review before this operation can continue. Contact the administrator.",
  DISPATCH_SIBLING_DRAFTS: "Other draft deliveries exist for this requisition. Cancel them safely before confirming this delivery.",
  INVALID_DISPATCH_MOVEMENT: invalidMovementMessage,
};

function StockMovementPopupContent({ requisitionId, businessUnitId, dispatchId, onClose }) {
  const access = useStockOperationAccess(businessUnitId);
  const generation = React.useRef(0);
  const guardedRequest = useCallback(async (method, ...args) => {
    const version = generation.current;
    const response = await axios[method](...args);
    if (version !== generation.current || !(access.can("requisitions", "view", true) || access.can("dispatched", "view", true))) {
      throw new Error("The document changed or your access changed. Close and reopen it before continuing.");
    }
    return response;
  }, [access]);
  const { languageId } = usePmsTheme();
  const t = getStockPopupTranslator(languageId);
  const dialog = useDocumentDialog();
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [req, setReq] = useState(null);
  const [deliveredDraft, setDeliveredDraft] = useState({});
  const dispatchNoteId = React.useRef(null);
  const [dispatchNoteCode, setDispatchNoteCode] = useState(null);
  const [context, setContext] = useState(null);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [canChooseSource, setCanChooseSource] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [pendingOperation, setPendingOperation] = useState(null);
  const operation = React.useRef(null);
  const draftValues = React.useRef({});
  const savedValues = React.useRef({});
  const [deliveryState, setDeliveryState] = useState("draft");
  const [saveUncertain, setSaveUncertain] = useState(false);

  // React state disables controls; the ref also guards events in the same render.
  const beginOperation = (name) => {
    if (operation.current) return false;
    operation.current = name;
    setPendingOperation(name);
    return true;
  };
  const finishOperation = () => {
    if (operation.current === "closed") return;
    operation.current = null;
    setPendingOperation(null);
  };
  const close = () => {
    operation.current = "closed";
    onClose();
  };
  const closeIfIdle = () => { if (!operation.current) close(); };

  useEffect(() => {
    let active = true;
    generation.current += 1;
    operation.current = null; setPendingOperation(null);
    setLoading(true);
    setLoadError("");
    setReq(null);
    setDeliveredDraft({});
    dispatchNoteId.current = null;
    draftValues.current = {};
    savedValues.current = {};
    setDeliveryState("draft");
    setSaveUncertain(false);
    setDispatchNoteCode(null);
    const load = async () => {
      try {
        console.log("[STOCK-MOV][LOAD] Loading requisition", requisitionId);

        const [res, contextRes, draftRes] = await Promise.all([
          guardedRequest("get", `/api/purchasing/stock-requisitions/${requisitionId}`, access.requestConfig("dispatched")),
          guardedRequest("get", "/api/purchasing/stock-requisitions/context", access.requestConfig("dispatched")),
          dispatchId ? guardedRequest("get", `/api/purchasing/stock-dispatch-notes/${dispatchId}`, access.requestConfig("dispatched")) : Promise.resolve(null),
        ]);
        if (!active) return;
        const rq = res.data;
        setCanChooseSource(!rq.source_location_id && !dispatchId);
        if (draftRes) {
          if (draftRes.data.status !== "draft") throw new Error("This dispatch is no longer available. Close and reopen the requisition.");
          dispatchNoteId.current = draftRes.data.id;
          setDispatchNoteCode(draftRes.data.dispatch_code);
        }
        const stockContext = contextRes.data;
        if (!Array.isArray(stockContext?.locations) || !Array.isArray(stockContext?.items) || !stockContext?.balances) {
          throw new Error("Invalid stock location context");
        }
        setContext(stockContext);
        const enrichedLines = (rq.lines || []).map((line) => ({
          ...line,
          stock_qty_initial: locationStockFor(stockContext, rq.source_location_id, line.item_id),
          unit_cost: stockContext.items.find((item) => Number(item.id) === Number(line.item_id))?.avg_cost ?? null,
          qty_delivered: line.qty_delivered || 0,
        }));
        if (draftRes) {
          enrichedLines.forEach((line, index) => {
            const saved = (draftRes.data.lines || draftRes.data.all_lines || []).find((entry) => Number(entry.stock_requisition_line_id || entry.requisition_line_id) === Number(line.id));
            if (saved && Number(saved.qty_dispatched) > 0) {
              draftValues.current[index] = String(saved.qty_dispatched);
              savedValues.current[index] = String(saved.qty_dispatched);
              line.qty_delivered = saved.qty_dispatched;
            }
          });
          setDeliveredDraft({ ...draftValues.current });
        }
        setReq({ ...rq, lines: enrichedLines });
      } catch (err) {
        if (!active) return;
        console.error("[STOCK-MOV][LOAD] Error:", err);
        setLoadError("Error loading requisition.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [requisitionId, dispatchId, businessUnitId, access, guardedRequest]);

  if (loading || !req) return <StockOperationsDialog title={t("Dispatch Stock Requisition")} onClose={closeIfIdle} busy={Boolean(pendingOperation)}><div className="so-modalBody"><h2 className="so-modalTitle">{t("Dispatch Stock Requisition")}</h2><p role={loadError ? "alert" : "status"}>{t(loadError || "Loading...")}</p><div className="so-modalFooter"><button type="button" className="so-btn so-btn--ghost" onClick={closeIfIdle} disabled={Boolean(pendingOperation)}>{t("Close")}</button></div></div></StockOperationsDialog>;

  const totalDeliveredCost = req.lines.reduce((sum, l) => {
    return sum + (Number(l.unit_cost) || 0) * (Number(l.qty_delivered) || 0);
  }, 0);
  const selectedLocation = context.locations.find((location) => Number(location.id) === Number(req.source_location_id));
  const canDispatchPolicy = req.permissions?.dispatch === true;
  const canDispatchSource = Boolean(selectedLocation && canDispatchPolicy && canUseStockLocation(context, req, "dispatch", selectedLocation.id));
  const dispatchLocations = context.locations.filter((location) => canUseStockLocation(context, req, "dispatch", location.id) &&
    `${location.name} ${location.code || ""}`.toLocaleLowerCase().includes(locationSearch.trim().toLocaleLowerCase()));

  const busy = Boolean(pendingOperation);
  const terminal = ["confirmed", "missing", "invalid"].includes(deliveryState);
  const editable = access.can("dispatched", "dispatch") && canDispatchSource && !busy && deliveryState === "draft";
  const uncertainResponse = (error) => !error.response || error.response.status >= 500;
  const operationError = (error, fallback) => t(dispatchErrorMessages[error.response?.data?.code]
    || error.response?.data?.error || error.message || fallback);
  const notifySaveError = async (error) => {
    if (error.response?.data?.code === "INVALID_DISPATCH_MOVEMENT") {
      setDeliveryState("invalid");
      setSaveUncertain(false);
    }
    await dialog.notify(operationError(error, "Error en entrega"));
  };

  const saveLine = async (index) => {
    const version = generation.current;
    const raw = draftValues.current[index];
    if (raw === undefined || (raw === "" && savedValues.current[index] === undefined) || savedValues.current[index] === raw) return;
    const qty = Number(raw);
    if (!Number.isFinite(qty) || qty <= 0) throw new Error(t("Delivered quantity must be greater than zero."));
    try {
      const res = await guardedRequest("post",
        `/api/purchasing/stock-requisitions/lines/${req.lines[index].id}/deliver`,
        { qty_delivered: qty, source_location_id: Number(req.source_location_id) },
        access.requestConfig("dispatched", "dispatch")
      );
      if (version !== generation.current || !access.can("dispatched", "view", true)) throw new Error("The document changed or your access changed. Close and reopen it before continuing.");
      if (res.data.stock_dispatch_note_id) {
        dispatchNoteId.current = res.data.stock_dispatch_note_id;
        setCanChooseSource(false);
        setDispatchNoteCode(res.data.stock_dispatch_note_code);
      }
      savedValues.current[index] = raw;
      setSaveUncertain(false);
      setReq((previous) => ({
        ...previous,
        stock_dispatch_note_id: res.data.stock_dispatch_note_id,
        stock_dispatch_note_code: res.data.stock_dispatch_note_code,
        lines: previous.lines.map((line, lineIndex) => lineIndex === index
          ? { ...line, qty_delivered: qty, stock_qty_initial: res.data.new_stock ?? line.stock_qty_initial } : line),
      }));
    } catch (error) {
      // The server may have saved a different quantity despite the lost reply.
      // Even retyping the last acknowledged value must issue a replacement save.
      if (uncertainResponse(error)) savedValues.current[index] = null;
      // A lost first response may already have created the draft. Recover its ID
      // with the same replacement save before allowing the popup to be discarded.
      if (uncertainResponse(error) && !dispatchNoteId.current) setSaveUncertain(true);
      throw error;
    }
  };
  const saveOnBlur = async (index) => {
    if (!editable || !beginOperation("saving")) return;
    try {
      await saveLine(index);
    } catch (error) {
      await notifySaveError(error);
    } finally {
      finishOperation();
    }
  };
  const recoverOperation = async (error, fallback, canceling = false) => {
    const uncertainState = canceling ? "cancel-unverified" : "unverified";
    let state = uncertainResponse(error) ? uncertainState : deliveryState;
    if (error.response?.data?.code === "INVALID_DISPATCH_MOVEMENT") state = "invalid";
    else if (error.response?.status === 404) state = "missing";
    else {
      try {
        const { data } = await guardedRequest("get", `/api/purchasing/stock-dispatch-notes/${dispatchNoteId.current}`, access.requestConfig("dispatched"));
        if (data.status === "confirmed") state = "confirmed";
        // A plain GET cannot prove a timed-out mutation has rolled back.
        else if (data.status === "draft" && !uncertainResponse(error) && !deliveryState.endsWith("unverified")) state = "draft";
      } catch (readError) {
        if (readError.response?.status === 404) state = "missing";
        else state = uncertainState;
      }
    }
    setDeliveryState(state);
    await dialog.notify(t(state === "invalid" ? invalidMovementMessage
      : state === "confirmed" ? "This delivery is already confirmed and cannot be canceled."
      : state === "missing" ? "This dispatch is no longer available. Close and reopen the requisition."
        : state === "cancel-unverified" ? "Cancellation could not be verified. Retry cancellation before changing this delivery."
          : state === "unverified" ? "Delivery status could not be verified. Retry confirmation or cancellation before changing quantities."
          : operationError(error, fallback)));
  };
  const cancelDelivery = async () => {
    if (saveUncertain || !beginOperation("canceling")) return;
    try {
      if (terminal || !dispatchNoteId.current || !access.can("dispatched", "cancel", true) || !canDispatchPolicy) { close(); return; }
      await guardedRequest("delete", `/api/purchasing/stock-dispatch-notes/${dispatchNoteId.current}`, access.requestConfig("dispatched", "cancel"));
      close();
    } catch (error) {
      await recoverOperation(error, "Error deleting SDN. Check console.", true);
    } finally {
      finishOperation();
    }
  };
  const confirmDelivery = async () => {
    if (!access.can("dispatched", "finalize", true) || !canDispatchSource || terminal || deliveryState === "cancel-unverified" || !beginOperation("confirming")) return;
    let finalizing = false;
    try {
      // Save explicitly and await the actual requests, including the focused
      // input. No synthetic blur, render delay or captured pre-save ID is needed.
      if (deliveryState === "draft" && access.can("dispatched", "dispatch", true)) {
        for (let index = 0; index < req.lines.length; index += 1) await saveLine(index);
      }
      if (!dispatchNoteId.current) {
        await dialog.notify(t("Enter a delivered quantity before confirming."));
        return;
      }
      finalizing = true;
      await guardedRequest("post", `/api/purchasing/stock-dispatch-notes/finalize/${dispatchNoteId.current}`, {}, access.requestConfig("dispatched", "finalize"));
      setDeliveryState("confirmed");
      await dialog.notify(t("Delivery confirmed successfully."));
      close();
    } catch (error) {
      if (finalizing) await recoverOperation(error, "Error confirming delivery.");
      else await notifySaveError(error);
    } finally {
      finishOperation();
    }
  };

  return (
    <StockOperationsDialog title={t("Dispatch Stock Requisition")} onClose={closeIfIdle} dismissible={false} busy={busy}>
        <div className="so-modalBody">
          <div className="so-modalHeader">
            <div>
              <div className="so-modalKicker">{t("Stock Dispatched")}</div>
              <h2 className="so-modalTitle">{t("Dispatch Stock Requisition")}</h2>
              <p className="so-modalSubtitle">
                {t("Confirm delivered quantities line by line and finalize the stock dispatch note once everything is ready.")}
              </p>
            </div>

            <div className="so-modalBadge">
              {dispatchNoteCode || req.stock_dispatch_note_code || t("Pending Dispatch")} - {req.requisition_code}
            </div>
          </div>

          <div className="so-field">
            <label className="so-label" htmlFor="stock-dispatch-source">{t("Source location")}</label>
            <input id="stock-dispatch-source" className="so-input" readOnly
              value={selectedLocation?.name || req.source_location?.name || ""} />
            {canChooseSource && (
              <button type="button" className="so-btn" disabled={!access.can("dispatched", "dispatch") || !canDispatchPolicy || busy || deliveryState !== "draft"} onClick={() => { if (!operation.current) setSourcePickerOpen(true); }}>
                {t(req.source_location_id ? "Source location" : "Source location is required.")}
              </button>
            )}
            <p className="so-muted">{t("The requested stock will be issued from this location for final consumption.")}</p>
            {req.source_location_id && !canDispatchSource && <p role="alert">{t("You cannot dispatch stock from this location.")}</p>}
          </div>

          <div className="so-summaryBar">
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Lines")}</div>
              <div className="so-summaryItemValue">{req.lines.length}</div>
            </div>
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Pending Qty")}</div>
              <div className="so-summaryItemValue">
                {formatStockOpsNumber(
                  req.lines.reduce((sum, line) => sum + Number(line.pending || 0), 0)
                )}
              </div>
            </div>
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Delivered Cost")}</div>
              <div className="so-summaryItemValue">
                {formatStockOpsCurrency(totalDeliveredCost)}
              </div>
            </div>
          </div>

          <div className="so-tableWrap so-tableWrap--modal">
            <table className="so-table">
              <thead>
                <tr>
                  <th>{t("Family")}</th>
                  <th>{t("Subfamily")}</th>
                  <th>{t("Category")}</th>
                  <th>{t("Subcategory")}</th>
                  <th>{t("Item")}</th>
                  <th className="so-number">{t("Stock")}</th>
                  <th className="so-number">{t("Requested")}</th>
                  <th className="so-number">{t("Pending")}</th>
                  <th className="so-number">{t("Unit Cost")}</th>
                  <th className="so-number">{t("Delivered")}</th>
                  <th className="so-number">{t("Delivered Cost")}</th>
                </tr>
              </thead>

              <tbody>
                {req.lines.map((line, index) => {
                  const item = line.item || {};
                  const category = item.category || {};
                  const subcategory = item.subcategory || {};
                  const subfamily = category.subfamily || subcategory.category?.subfamily || {};
                  const family = subfamily.family || {};

                  return (
                    <tr key={index}>
                      <td className="so-muted">{family?.name || "-"}</td>
                      <td className="so-muted">{subfamily?.name || "-"}</td>
                      <td className="so-muted">{category?.name || "-"}</td>
                      <td className="so-muted">{subcategory?.name || "-"}</td>
                      <td className="so-strong">{item?.name || "-"}</td>
                      <td className="so-number">{line.stock_qty_initial == null ? "-" : formatStockOpsNumber(line.stock_qty_initial)}</td>
                      <td className="so-number">{formatStockOpsNumber(line.quantity)}</td>
                      <td className="so-number">{formatStockOpsNumber(line.pending)}</td>
                      <td className="so-number">{formatStockOpsCurrency(line.unit_cost)}</td>
                      <td className="so-number">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          disabled={!editable}
                          className="so-input so-input--qty so-number"
                          aria-label={`${t("Delivered")} · ${item?.name || `${t("Line")} ${index + 1}`}`}
                          value={deliveredDraft[index] ?? ""}
                          onChange={(e) => {
                            draftValues.current[index] = e.target.value;
                            setDeliveredDraft((previous) => ({ ...previous, [index]: e.target.value }));
                          }}
                          onBlur={() => saveOnBlur(index)}
                        />
                      </td>
                      <td className="so-number">
                        {formatStockOpsCurrency(
                          (Number(line.unit_cost) || 0) * (Number(line.qty_delivered) || 0)
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!req.lines.length && (
                  <tr>
                    <td colSpan="11" className="so-empty">
                      {t("No dispatchable lines were found for this requisition.")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {saveUncertain && <p role="alert">{t("Retry saving the delivery before canceling; its first response was not received.")}</p>}
          {deliveryState === "unverified" && <p role="alert">{t("Delivery status could not be verified. Retry confirmation or cancellation before changing quantities.")}</p>}
          {deliveryState === "cancel-unverified" && <p role="alert">{t("Cancellation could not be verified. Retry cancellation before changing this delivery.")}</p>}
          {terminal && <p role="status">{t(deliveryState === "invalid" ? invalidMovementMessage
            : deliveryState === "confirmed" ? "This delivery is already confirmed and cannot be canceled."
              : "This dispatch is no longer available. Close and reopen the requisition.")}</p>}
          <div className="so-modalFooter">
            <button type="button" className="so-btn so-btn--danger"
              disabled={busy || saveUncertain}
              onMouseDown={(event) => event.preventDefault()}
              onClick={cancelDelivery}>
              {t(terminal || !access.can("dispatched", "cancel") || !canDispatchPolicy ? "Close" : "Close & Cancel")}
            </button>
            <button type="button" className="so-btn so-btn--success"
              disabled={!access.can("dispatched", "finalize") || !canDispatchSource || busy || terminal || deliveryState === "cancel-unverified"}
              onMouseDown={(event) => event.preventDefault()}
              onClick={confirmDelivery}>
              {t("Confirm Delivery")}
            </button>
          </div>
        </div>
      {dialog.dialog}
      {sourcePickerOpen && <StockOperationsDialog title={t("Source location")} onClose={() => { if (!operation.current) setSourcePickerOpen(false); }} busy={busy}>
        <div className="so-modalBody">
          <h2 className="so-modalTitle">{t("Source location")}</h2>
          <input type="search" className="so-input" aria-label={t("Search locations...")}
            placeholder={t("Search locations...")} value={locationSearch} onChange={(event) => setLocationSearch(event.target.value)} />
          <div className="so-pickerList">
            {dispatchLocations.map((location) => <button type="button" className="so-pickerOption" key={location.id} disabled={busy}
              onClick={() => {
                if (operation.current) return;
                setReq((previous) => ({ ...previous, source_location_id: location.id, source_location: location,
                  lines: previous.lines.map((line) => ({ ...line, stock_qty_initial: locationStockFor(context, location.id, line.item_id) })) }));
                setSourcePickerOpen(false);
              }}>
              <span className="so-pickerOptionMain"><strong>{location.name}</strong><span>{location.code}</span></span>
            </button>)}
            {!dispatchLocations.length && <p className="so-empty">{t("No locations available for dispatch.")}</p>}
          </div>
          <div className="so-modalFooter"><button type="button" className="so-btn" disabled={busy} onClick={() => { if (!operation.current) setSourcePickerOpen(false); }}>{t("Close")}</button></div>
        </div>
      </StockOperationsDialog>}
    </StockOperationsDialog>
  );
}

export default function StockMovementPopup(props) {
  return <StockMovementPopupContent key={`${props.businessUnitId || ""}:${props.requisitionId}:${props.dispatchId || "new"}`} {...props} />;
}
